import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Bell, Search, Filter, CheckCircle, AlertTriangle, 
  Info, Eye, MoreVertical, Trash2, ExternalLink
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNotifications } from '@/hooks/useNotifications';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const notifications = useNotifications();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLevel, setFilterLevel] = useState<string>('all');

  const getIcon = (level: string) => {
    switch (level) {
      case 'error': return <AlertTriangle className="h-4 w-4 text-danger" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-warning" />;
      case 'success': return <CheckCircle className="h-4 w-4 text-success" />;
      default: return <Info className="h-4 w-4 text-brand" />;
    }
  };

  const getLevelBadge = (level: string) => {
    const colors = {
      error: 'bg-danger/10 text-danger border-danger/20',
      warning: 'bg-warning/10 text-warning border-warning/20', 
      success: 'bg-success/10 text-success border-success/20',
      info: 'bg-brand/10 text-brand border-brand/20',
    };
    
    return (
      <Badge className={`${colors[level as keyof typeof colors] || colors.info} text-xs`}>
        {level.charAt(0).toUpperCase() + level.slice(1)}
      </Badge>
    );
  };

  const filteredNotifications = notifications.data.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         notification.body?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterLevel === 'all' || notification.level === filterLevel;
    return matchesSearch && matchesFilter;
  });

  const handleNotificationClick = (notification: any) => {
    // Mark as read if not already read
    if (!notification.read_at) {
      notifications.markAsRead([notification.id]);
    }
    
    // Navigate to action URL if available
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Bell className="h-8 w-8 text-brand" />
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Notifications</h1>
            <p className="text-text-secondary">Stay updated with important system events</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Badge className="bg-chip-bg text-chip-text">
            {notifications.unreadCount} unread
          </Badge>
          {notifications.unreadCount > 0 && (
            <Button
              variant="outline"
              onClick={() => notifications.markAllAsRead()}
              disabled={notifications.isMarkingAllRead}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark All Read
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filter Bar */}
      <Card className="bg-bg-elevated border-border-subtle">
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-muted" />
              <Input
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-bg-subtle border-border-subtle focus:border-brand"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-text-muted" />
              <select
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value)}
                className="px-3 py-2 bg-bg-subtle border border-border-subtle rounded-md text-text-primary focus:border-brand focus:outline-none"
              >
                <option value="all">All Levels</option>
                <option value="error">Error</option>
                <option value="warning">Warning</option>
                <option value="success">Success</option>
                <option value="info">Info</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications List */}
      <Card className="bg-bg-elevated border-border-subtle">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>All Notifications ({filteredNotifications.length})</span>
            {filteredNotifications.length > 0 && (
              <span className="text-sm font-normal text-text-secondary">
                Showing {filteredNotifications.length} of {notifications.data.length} notifications
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="max-h-[600px]">
            <div className="divide-y divide-border-subtle">
              {filteredNotifications.map((notification, index) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-hover-bg transition-colors cursor-pointer ${
                    !notification.read_at ? 'bg-brand/5 border-l-2 border-l-brand' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start justify-between space-x-4">
                    <div className="flex items-start space-x-3 flex-1">
                      {getIcon(notification.level)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className={`text-sm font-medium ${
                            !notification.read_at ? 'text-text-primary' : 'text-text-secondary'
                          }`}>
                            {notification.title}
                          </h3>
                          {getLevelBadge(notification.level)}
                          {!notification.read_at && (
                            <div className="w-2 h-2 bg-brand rounded-full" />
                          )}
                        </div>
                        
                        {notification.body && (
                          <p className="text-sm text-text-muted mb-2 line-clamp-2">
                            {notification.body}
                          </p>
                        )}
                        
                        <div className="flex items-center space-x-4 text-xs text-text-muted">
                          <span>
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {notification.type}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="p-1 h-auto">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!notification.read_at && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              notifications.markAsRead([notification.id]);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Mark as Read
                          </DropdownMenuItem>
                        )}
                        {notification.action_url && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(notification.action_url!);
                            }}
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Open Link
                          </DropdownMenuItem>
                        )}
                        <Separator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Implement delete functionality
                          }}
                          className="text-danger focus:text-danger"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
              
              {filteredNotifications.length === 0 && (
                <div className="text-center py-12">
                  <Bell className="h-12 w-12 mx-auto text-text-muted opacity-50 mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    {notifications.data.length === 0 ? 'No notifications yet' : 'No matching notifications'}
                  </h3>
                  <p className="text-text-muted">
                    {notifications.data.length === 0 
                      ? 'You\'ll see important updates and alerts here when they arrive.'
                      : 'Try adjusting your search or filter criteria.'
                    }
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;